import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { FaFolder, FaComments, FaUserShield, FaFileCode, FaCog, FaSignOutAlt, FaHome } from "react-icons/fa";
import { ToastContainer, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Profile from "./Profile";
import { Avatar, Tooltip } from "@mui/material";
import api from "./api/api";

const AppContent = () => {
	const logo = "/MILLER_icon.png";
	const navigate = useNavigate();
	const location = useLocation();
	const rank = Number(sessionStorage.getItem("rank"));
	const [showProfile, setShowProfile] = useState(false);
	const [ghostActive, setGhostActive] = useState(sessionStorage.getItem("ghost") === "true");
	const [tokensRemaining, setTokensRemaining] = useState(null);

	const handleLogout = () => {
		sessionStorage.clear();
		localStorage.removeItem("logged");
		setShowProfile(false);
		navigate("/login");
	};

	useEffect(() => {
		const handleGhostChange = () => setGhostActive(sessionStorage.getItem("ghost") === "true");
		window.addEventListener("ghost-changed", handleGhostChange);
		return () => window.removeEventListener("ghost-changed", handleGhostChange);
	}, []);

	useEffect(() => {
		const fetchTokens = async () => {
			if (rank !== 0) {
				setTokensRemaining(null);
				return;
			}
			const username = sessionStorage.getItem("username");
			if (!username) return;
			try {
				const res = await api.get("users", { params: { username } });
				const tokenValue = res?.data?.[0]?.token;
				setTokensRemaining(Number.isFinite(Number(tokenValue)) ? Number(tokenValue) : null);
			} catch (error) {
				setTokensRemaining(null);
			}
		};

		fetchTokens();
		const handleTokensUpdate = () => fetchTokens();
		window.addEventListener("tokens-updated", handleTokensUpdate);
		const intervalId = rank === 0 ? setInterval(fetchTokens, 10000) : null;
		return () => {
			window.removeEventListener("tokens-updated", handleTokensUpdate);
			if (intervalId) clearInterval(intervalId);
		};
	}, [rank, ghostActive]);

	const handleGhostExit = async () => {
		try {
			const res = await api.post("admin/users/ghost-exit", {
				adminUsername: sessionStorage.getItem("ghostAdminUsername") || "",
			});
			sessionStorage.setItem("logged", "ok");
			sessionStorage.setItem("username", res.data.username);
			sessionStorage.setItem("email", res.data.email);
			sessionStorage.setItem("nome", `${res.data.nome} ${res.data.cognome}`.trim());
			sessionStorage.setItem("rank", res.data.rank);
			sessionStorage.removeItem("ghost");
			sessionStorage.removeItem("ghostAdminUsername");
			sessionStorage.removeItem("ghostAdminEmail");
			sessionStorage.removeItem("ghostAdminNome");
			sessionStorage.removeItem("ghostAdminRank");
			window.dispatchEvent(new Event("ghost-changed"));
			navigate("/Homepage");
		} catch (error) {
			console.error(error);
		}
	};

	const overviewItems = useMemo(
		() => [
			{ label: "Homepage", icon: <FaHome />, to: "/Homepage" },
			{ label: "Progetti", icon: <FaFolder />, to: "/Progetti" },
			{ label: "Chatbot", icon: <FaComments />, to: "/Jobs" },
		],
		[]
	);

	const adminItems = useMemo(
		() =>
			rank === 1
				? [
						{ label: "Utenze", icon: <FaUserShield />, to: "/Utenze" },
						{ label: "Prompts", icon: <FaFileCode />, to: "/Prompts" },
					]
				: [],
		[rank]
	);

	return (
		<>
			<ToastContainer
				position="top-right"
				autoClose={3000}
				hideProgressBar={true}
				newestOnTop={false}
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				draggable
				pauseOnHover
				theme="dark"
				toastStyle={{ backgroundColor: "var(--brand-light)", color: "#fff" }}
				toastClassName="toast-white-text"
				bodyClassName="toast-white-text"
				transition={Slide}
			/>
			<div className="h-screen flex" style={{ background: "linear-gradient(135deg, rgba(141,196,235,0.45) 0%, rgba(35,100,174,0.25) 40%, rgba(39,55,62,0.2) 100%)" }}>
				<aside
					className="w-60 flex flex-col justify-between py-6 px-4 shadow-xl"
					style={{ background: "linear-gradient(180deg, #f7f9fc 0%, #eef1f7 100%)", color: "var(--brand-dark)", borderRight: "1px solid rgba(39,55,62,0.08)" }}
				>
					<div className="flex flex-col gap-6">
						<div className="flex items-center gap-3 px-2">
							<img src={logo} alt="Miller" className="h-10 w-auto" />
							<div>
								<p className="text-sm opacity-70">Miller Group</p>
								<p className="text-lg font-semibold">Prophet AI</p>
							</div>
						</div>

						<div className="flex flex-col gap-2">
							<p className="text-xs tracking-wide font-semibold px-2 opacity-60">WORKSPACE</p>
							{overviewItems.map((item) => {
								const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
								const activeBg = "rgba(38,42,115,0.12)";
								const textColor = active ? "rgba(39,55,62,0.95)" : "rgba(39,55,62,0.9)";
								return (
									<button
										key={item.to}
										onClick={() => navigate(item.to)}
										className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold transition-all"
										style={{
											background: active ? activeBg : "transparent",
											color: textColor,
											border: active ? "1px solid rgba(38,42,115,0.16)" : "1px solid transparent",
											boxShadow: active ? "0 4px 10px rgba(0,0,0,0.08)" : "none",
										}}
									>
										<span className="text-lg" style={{ color: active ? "rgba(39,55,62,0.95)" : "rgba(39,55,62,0.7)" }}>{item.icon}</span>
										<span>{item.label}</span>
									</button>
								);
							})}
						</div>

						{adminItems.length > 0 && (
							<div className="flex flex-col gap-2">
								<p className="text-xs tracking-wide font-semibold px-2 opacity-60">ADMIN</p>
								{adminItems.map((item, idx) => {
									const active = item.to && (location.pathname === item.to || location.pathname.startsWith(item.to + "/"));
									const activeBg = "rgba(38,42,115,0.12)";
									const textColor = active ? "rgba(39,55,62,0.95)" : "rgba(39,55,62,0.9)";
									return (
										<button
											key={item.to || `admin-${idx}`}
											onClick={() => (item.to ? navigate(item.to) : item.action())}
											className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold transition-all"
											style={{
												background: active ? activeBg : "transparent",
												color: textColor,
												border: active ? "1px solid rgba(38,42,115,0.16)" : "1px solid transparent",
												boxShadow: active ? "0 4px 10px rgba(0,0,0,0.08)" : "none",
											}}
										>
										<span className="text-lg" style={{ color: active ? "rgba(39,55,62,0.95)" : "rgba(39,55,62,0.7)" }}>{item.icon}</span>
											<span>{item.label}</span>
										</button>
									);
								})}
							</div>
						)}

						<div className="flex flex-col gap-3">
							<p className="text-xs tracking-wide font-semibold px-2 opacity-60">ACCOUNT</p>
							<div className="flex items-center gap-3 px-3">
								<Tooltip title={sessionStorage.getItem("email") || ""} placement="right">
									<Avatar sx={{ width: 32, height: 32, bgcolor: "rgba(35,100,174,0.8)", cursor: "default" }}>
										{(sessionStorage.getItem("nome") || "U")[0]}
									</Avatar>
								</Tooltip>
								<div className="flex flex-col leading-tight">
									<span className="text-sm font-semibold">
										{sessionStorage.getItem("nome") || "Utente"}
										{rank === 0 && tokensRemaining !== null ? ` · ${tokensRemaining} token` : ""}
									</span>
								</div>
							</div>
							{ghostActive && (
								<div className="flex flex-col gap-2 px-3">
									<span className="text-xs uppercase tracking-wide font-semibold" style={{ color: "rgba(39,55,62,0.8)" }}>
										Ghost attivo
									</span>
									<button
										type="button"
										onClick={handleGhostExit}
										className="w-full rounded-xl px-3 py-2 text-xs font-semibold uppercase"
										style={{ background: "rgba(35,100,174,0.18)", color: "var(--brand-dark)" }}
									>
										Esci dal ghost
									</button>
								</div>
							)}
						</div>
					</div>

					<div className="flex flex-col gap-2">
						<button
							onClick={() => setShowProfile(true)}
							className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold transition-all"
							style={{ color: "rgba(39,55,62,0.9)" }}
						>
							<FaCog className="text-lg" />
							<span>Impostazioni</span>
						</button>
						<button
							onClick={handleLogout}
							className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold transition-all"
							style={{ color: "#c0392b" }}
						>
							<FaSignOutAlt className="text-lg" />
							<span>Logout</span>
						</button>
					</div>
				</aside>

				<div className="flex-1 flex flex-col">
					<main className="p-2 md:p-4 z-0 duration-300 h-full overflow-x-hidden overflow-y-auto">
						<Outlet />
					</main>
				</div>
				{showProfile && <Profile onClose={() => setShowProfile(false)} onLogout={handleLogout} />}
			</div>
		</>
	);
};

export default AppContent;
